// src/modules/ImageData.jsx
// Real image processing using Canvas API
// Upload image → pixel viz → channel stats → histogram → filters → comparison

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, RadarChart,
  Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  applyFilter, getChannelHistograms, getChannelStats,
  getPixelGrid, getImageMetadata,
} from '../../utils/imageProcessing.js';
import { Panel, Metric, Tag, Spinner, InfoBox, Tabs, timestamp } from '../../components/ui.jsx';

const SAMPLE_IMAGES = [
  { name: 'Gradient Test', type: 'gradient' },
  { name: 'Geometric Shapes', type: 'shapes' },
  { name: 'Color Spectrum', type: 'spectrum' },
  { name: 'Noise Pattern', type: 'noise' },
  { name: 'Checkerboard', type: 'checker' },
];

// Generate synthetic test images on a canvas
const generateSyntheticImage = (type, size = 256) => {
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');

  switch (type) {
    case 'gradient': {
      const g = ctx.createLinearGradient(0, 0, size, size);
      g.addColorStop(0, '#00d9ff'); g.addColorStop(0.5, '#b08aff'); g.addColorStop(1, '#ff3556');
      ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
      const g2 = ctx.createRadialGradient(size/2, size/2, 0, size/2, size/2, size/2);
      g2.addColorStop(0, 'rgba(255,255,255,0.3)'); g2.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g2; ctx.fillRect(0, 0, size, size);
      break;
    }
    case 'shapes': {
      ctx.fillStyle = '#050e16'; ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#00d9ff'; ctx.fillRect(20, 20, 80, 80);
      ctx.fillStyle = '#ff3556'; ctx.beginPath(); ctx.arc(190, 70, 50, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = '#29ffa5'; ctx.beginPath(); ctx.moveTo(128, 140); ctx.lineTo(200, 230); ctx.lineTo(56, 230); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#ffbe30'; ctx.fillRect(30, 160, 70, 70);
      ctx.fillStyle = '#b08aff'; ctx.beginPath(); ctx.arc(70, 200, 20, 0, Math.PI*2); ctx.fill();
      break;
    }
    case 'spectrum': {
      for (let x = 0; x < size; x++) {
        const hue = (x / size) * 360;
        ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
        ctx.fillRect(x, 0, 1, size / 2);
      }
      for (let x = 0; x < size; x++) {
        const hue = (x / size) * 360;
        ctx.fillStyle = `hsl(${hue}, 100%, ${25 + (x/size)*50}%)`;
        ctx.fillRect(x, size / 2, 1, size / 2);
      }
      break;
    }
    case 'noise': {
      const id = ctx.createImageData(size, size);
      for (let i = 0; i < id.data.length; i += 4) {
        const v = Math.random() * 255;
        id.data[i] = v; id.data[i+1] = Math.random()*255; id.data[i+2] = Math.random()*255; id.data[i+3] = 255;
      }
      ctx.putImageData(id, 0, 0);
      break;
    }
    case 'checker': {
      const sq = 32;
      for (let y = 0; y < size; y += sq) {
        for (let x = 0; x < size; x += sq) {
          ctx.fillStyle = ((x/sq + y/sq) % 2 === 0) ? '#e2f0f7' : '#050e16';
          ctx.fillRect(x, y, sq, sq);
        }
      }
      break;
    }
  }

  return canvas.toDataURL('image/png');
};

const FILTERS = [
  { id: 'original', label: 'Original', color: 'var(--t2)' },
  { id: 'grayscale', label: 'Grayscale', color: 'var(--t2)' },
  { id: 'invert', label: 'Invert', color: 'var(--violet)' },
  { id: 'red_channel', label: 'Red Ch.', color: 'var(--red)' },
  { id: 'green_channel', label: 'Green Ch.', color: 'var(--green)' },
  { id: 'blue_channel', label: 'Blue Ch.', color: 'var(--blue)' },
  { id: 'sepia', label: 'Sepia', color: 'var(--gold)' },
  { id: 'sharpen', label: 'Sharpen', color: 'var(--cyan)' },
  { id: 'blur', label: 'Box Blur', color: 'var(--cyan)' },
  { id: 'edge_detect', label: 'Edge Detect', color: 'var(--cyan)' },
  { id: 'emboss', label: 'Emboss', color: 'var(--t2)' },
  { id: 'threshold', label: 'Threshold', color: 'var(--violet)' },
  { id: 'brightness', label: 'Brightness', color: 'var(--gold)' },
  { id: 'contrast', label: 'Contrast', color: 'var(--gold)' },
  { id: 'rotate_hue', label: 'Hue Shift', color: 'var(--violet)' },
  { id: 'pixelate', label: 'Pixelate', color: 'var(--orange)' },
  { id: 'noise', label: 'Add Noise', color: 'var(--red)' },
];

const IMG_TABS = [
  { id: 'upload', label: 'UPLOAD', icon: '⬆' },
  { id: 'filters', label: 'FILTERS', icon: '◈', requiresData: true },
  { id: 'channels', label: 'CHANNELS', icon: '◉', requiresData: true },
  { id: 'histogram', label: 'HISTOGRAM', icon: '⬟', requiresData: true },
  { id: 'pixels', label: 'PIXEL GRID', icon: '⬡', requiresData: true },
  { id: 'compare', label: 'COMPARE', icon: '◫', requiresData: true },
];

export default function ImageDataModule({ mode }) {
  const [tab, setTab] = useState('upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [imgEl, setImgEl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Filter state
  const [activeFilter, setActiveFilter] = useState('original');
  const [filteredSrc, setFilteredSrc] = useState(null);
  const [filterParams, setFilterParams] = useState({
    brightness: 1.3, contrast: 1.5, threshold: 128, hue: 90, blockSize: 10, noise: 30
  });

  // Analysis state
  const [channelHist, setChannelHist] = useState([]);
  const [channelStats, setChannelStats] = useState(null);
  const [pixelGrid, setPixelGrid] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [pixelGridSize, setPixelGridSize] = useState(24);
  const [hoveredPixel, setHoveredPixel] = useState(null);

  const fileRef = useRef();

  const loadImage = useCallback((src, file = null) => {
    setProcessing(true);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImgEl(img);
      setImgSrc(src);
      setFilteredSrc(src);
      setMetadata(getImageMetadata(img, file));
      setChannelHist(getChannelHistograms(img));
      setChannelStats(getChannelStats(img));
      setPixelGrid(getPixelGrid(img, 24));
      setActiveFilter('original');
      setProcessing(false);
      setTab('filters');
    };
    img.onerror = () => setProcessing(false);
    img.src = src;
  }, []);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImgFile(file);
    const reader = new FileReader();
    reader.onload = e => loadImage(e.target.result, file);
    reader.readAsDataURL(file);
  };

  const loadSynthetic = (type) => {
    const src = generateSyntheticImage(type, 256);
    loadImage(src, null);
  };

  // Apply filter when activeFilter or params change
  useEffect(() => {
    if (!imgEl || activeFilter === 'original') { setFilteredSrc(imgSrc); return; }
    const paramsForFilter = {
      brightness: filterParams.brightness,
      contrast: filterParams.contrast,
      threshold: filterParams.threshold,
      hue: filterParams.hue,
      blockSize: Math.round(filterParams.blockSize),
      noise: filterParams.noise,
    };
    try {
      const result = applyFilter(imgEl, activeFilter, paramsForFilter);
      setFilteredSrc(result);
    } catch (e) { console.error('Filter error:', e); }
  }, [activeFilter, filterParams, imgEl]);

  // Recompute pixel grid when size changes
  useEffect(() => {
    if (!imgEl) return;
    setPixelGrid(getPixelGrid(imgEl, pixelGridSize));
  }, [pixelGridSize, imgEl]);

  const hasParams = ['brightness', 'contrast', 'threshold', 'rotate_hue', 'pixelate', 'noise'].includes(activeFilter);

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <Tabs tabs={IMG_TABS} active={tab} onChange={setTab} dataset={imgSrc} />

      {/* ══ UPLOAD ══ */}
      {tab === 'upload' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${isDragging ? 'var(--blue)' : 'var(--b1)'}`, borderRadius: '4px', padding: '52px 24px', textAlign: 'center', cursor: 'pointer', background: isDragging ? 'rgba(96,165,250,.08)' : 'var(--surface)', transition: 'all .2s', animation: 'float 3s ease infinite' }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
            {processing
              ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}><Spinner size={24} /><span style={{ color: 'var(--blue)', fontSize: '12px' }}>LOADING IMAGE...</span></div>
              : <>
                  <div style={{ fontSize: '40px', marginBottom: '14px', opacity: .4 }}>◈</div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '18px', color: isDragging ? 'var(--blue)' : 'var(--t1)', marginBottom: '7px' }}>Drop Image File Here</div>
                  <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '18px' }}>PNG, JPG, WEBP, GIF supported</div>
                </>}
          </div>

          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--blue)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '12px' }}>Use generated images to explore pixel manipulations without uploading:</div>
            {SAMPLE_IMAGES.map(({ name, type }) => (
              <div key={type} onClick={() => loadSynthetic(type)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', border: '1px solid var(--b0)', borderRadius: '3px', marginBottom: '6px', cursor: 'pointer', background: 'var(--raised)', transition: 'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.background = 'var(--hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--b0)'; e.currentTarget.style.background = 'var(--raised)'; }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '2px', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--b1)' }}>
                  <canvas ref={el => { if (el && !el._drawn) { el._drawn = true; const c2 = document.createElement('canvas'); c2.width = 36; c2.height = 36; const ctx = c2.getContext('2d'); const tmp = new Image(); tmp.onload = () => { ctx.drawImage(tmp, 0, 0, 36, 36); el.getContext('2d').drawImage(c2, 0, 0); }; tmp.src = generateSyntheticImage(type, 64); } }} width={36} height={36} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)' }}>256×256 · {type} pattern</div>
                </div>
                <span style={{ color: 'var(--blue)', fontSize: '12px' }}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* ══ FILTERS ══ */}
      {tab === 'filters' && imgSrc && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          {/* Original + Filtered side by side */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="ORIGINAL" badge={metadata?.fileName}>
              <div style={{ textAlign: 'center' }}>
                <img src={imgSrc} alt="original" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '2px', border: '1px solid var(--b1)' }} />
              </div>
              {metadata && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '10px' }}>
                  <Metric label="Width" value={metadata.width} unit="px" color="var(--blue)" />
                  <Metric label="Height" value={metadata.height} unit="px" color="var(--blue)" />
                  <Metric label="Megapixels" value={metadata.megapixels} unit="MP" color="var(--blue)" />
                </div>
              )}
            </Panel>

            {/* Filter params */}
            {hasParams && (
              <Panel title="FILTER PARAMETERS">
                {activeFilter === 'brightness' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>Brightness factor</span>
                      <span style={{ color: 'var(--gold)', fontSize: '11px', fontWeight: 600 }}>{filterParams.brightness.toFixed(2)}×</span>
                    </div>
                    <input type="range" min={0.1} max={3} step={0.05} value={filterParams.brightness} onChange={e => setFilterParams(p => ({ ...p, brightness: parseFloat(e.target.value) }))} style={{ width: '100%', accentColor: 'var(--gold)' }} />
                  </div>
                )}
                {activeFilter === 'contrast' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>Contrast factor</span>
                      <span style={{ color: 'var(--gold)', fontSize: '11px', fontWeight: 600 }}>{filterParams.contrast.toFixed(2)}×</span>
                    </div>
                    <input type="range" min={0.1} max={4} step={0.05} value={filterParams.contrast} onChange={e => setFilterParams(p => ({ ...p, contrast: parseFloat(e.target.value) }))} style={{ width: '100%', accentColor: 'var(--gold)' }} />
                  </div>
                )}
                {activeFilter === 'threshold' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>Threshold value</span>
                      <span style={{ color: 'var(--violet)', fontSize: '11px', fontWeight: 600 }}>{filterParams.threshold}</span>
                    </div>
                    <input type="range" min={0} max={255} step={1} value={filterParams.threshold} onChange={e => setFilterParams(p => ({ ...p, threshold: parseInt(e.target.value) }))} style={{ width: '100%', accentColor: 'var(--violet)' }} />
                  </div>
                )}
                {activeFilter === 'rotate_hue' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>Hue rotation</span>
                      <span style={{ color: 'var(--violet)', fontSize: '11px', fontWeight: 600 }}>{filterParams.hue}°</span>
                    </div>
                    <input type="range" min={0} max={360} step={5} value={filterParams.hue} onChange={e => setFilterParams(p => ({ ...p, hue: parseInt(e.target.value) }))} style={{ width: '100%', accentColor: 'var(--violet)' }} />
                  </div>
                )}
                {activeFilter === 'pixelate' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>Block size</span>
                      <span style={{ color: 'var(--orange)', fontSize: '11px', fontWeight: 600 }}>{Math.round(filterParams.blockSize)}px</span>
                    </div>
                    <input type="range" min={2} max={32} step={1} value={filterParams.blockSize} onChange={e => setFilterParams(p => ({ ...p, blockSize: parseInt(e.target.value) }))} style={{ width: '100%', accentColor: 'var(--orange)' }} />
                  </div>
                )}
                {activeFilter === 'noise' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>Noise amount</span>
                      <span style={{ color: 'var(--red)', fontSize: '11px', fontWeight: 600 }}>{filterParams.noise}</span>
                    </div>
                    <input type="range" min={5} max={120} step={5} value={filterParams.noise} onChange={e => setFilterParams(p => ({ ...p, noise: parseInt(e.target.value) }))} style={{ width: '100%', accentColor: 'var(--red)' }} />
                  </div>
                )}
              </Panel>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="FILTER OUTPUT" badge={activeFilter.replace('_', ' ').toUpperCase()} accent="var(--blue)">
              <div style={{ textAlign: 'center' }}>
                <img src={filteredSrc || imgSrc} alt="filtered" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '2px', border: '1px solid var(--b1)' }} />
              </div>
              {filteredSrc && filteredSrc !== imgSrc && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <a href={filteredSrc} download={`${activeFilter}_result.png`}
                    style={{ padding: '7px 16px', background: 'var(--gcyan)', border: '1px solid var(--cyan)44', color: 'var(--cyan)', fontSize: '10px', borderRadius: '2px', textDecoration: 'none', letterSpacing: '.08em', fontFamily: 'JetBrains Mono' }}>
                    ↓ DOWNLOAD
                  </a>
                </div>
              )}
            </Panel>

            {/* Filter grid */}
            <Panel title="FILTER GALLERY" badge={`${FILTERS.length} filters`}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '5px' }}>
                {FILTERS.map(f => (
                  <button key={f.id} onClick={() => setActiveFilter(f.id)}
                    style={{ padding: '7px 6px', border: `1px solid ${activeFilter === f.id ? f.color : 'var(--b0)'}`, background: activeFilter === f.id ? f.color + '20' : 'var(--raised)', color: activeFilter === f.id ? f.color : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', textAlign: 'center', transition: 'all .15s' }}>
                    {f.label}
                  </button>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ CHANNELS ══ */}
      {tab === 'channels' && channelStats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
            {Object.entries(channelStats).map(([ch, s]) => (
              <Metric key={ch} label={`${ch} Channel`} value={parseFloat(s.mean).toFixed(1)} unit="mean"
                color={ch === 'R' ? 'var(--red)' : ch === 'G' ? 'var(--green)' : ch === 'B' ? 'var(--blue)' : 'var(--t2)'}
                sub={`σ=${s.std} range=[${s.min},${s.max}]`}
              />
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Panel title="CHANNEL IMAGES" badge="visual isolation" accent="var(--blue)">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[
                  { label: 'Original', filter: 'original', color: 'var(--t2)' },
                  { label: 'Grayscale', filter: 'grayscale', color: 'var(--t2)' },
                  { label: 'Red Channel', filter: 'red_channel', color: 'var(--red)' },
                  { label: 'Green Channel', filter: 'green_channel', color: 'var(--green)' },
                  { label: 'Blue Channel', filter: 'blue_channel', color: 'var(--blue)' },
                  { label: 'Edge Detect', filter: 'edge_detect', color: 'var(--cyan)' },
                ].map(({ label, filter, color }) => {
                  const src = filter === 'original' ? imgSrc : imgEl ? applyFilter(imgEl, filter) : null;
                  return (
                    <div key={filter} style={{ position: 'relative' }}>
                      <div style={{ fontSize: '9px', color, letterSpacing: '.08em', marginBottom: '4px' }}>{label}</div>
                      {src && <img src={src} alt={label} style={{ width: '100%', borderRadius: '2px', border: `1px solid ${color}44` }} />}
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel title="CHANNEL STATISTICS" accent="var(--blue)">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr style={{ background: 'var(--deep)' }}>
                    {['Channel', 'Mean', 'Std', 'Min', 'Max', 'Median'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', letterSpacing: '.08em', borderBottom: '1px solid var(--b0)' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {Object.entries(channelStats).map(([ch, s]) => (
                      <tr key={ch} style={{ borderBottom: '1px solid var(--b0)' }}>
                        <td style={{ padding: '7px 10px', color: ch === 'R' ? 'var(--red)' : ch === 'G' ? 'var(--green)' : ch === 'B' ? 'var(--blue)' : 'var(--t2)', fontWeight: 600 }}>{ch}</td>
                        {[s.mean, s.std, s.min, s.max, s.median].map((v, i) => (
                          <td key={i} style={{ padding: '7px 10px', color: 'var(--t2)' }}>{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '10px', color: 'var(--t3)', letterSpacing: '.1em', marginBottom: '10px' }}>CHANNEL RADAR</div>
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart data={[
                    { metric: 'Mean', R: channelStats.R.mean, G: channelStats.G.mean, B: channelStats.B.mean },
                    { metric: 'Std', R: channelStats.R.std, G: channelStats.G.std, B: channelStats.B.std },
                    { metric: 'Max', R: channelStats.R.max, G: channelStats.G.max, B: channelStats.B.max },
                  ]}>
                    <PolarGrid stroke="var(--b1)" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--t3)', fontSize: 10 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 255]} tick={{ fill: 'var(--t3)', fontSize: 9 }} />
                    <Radar name="R" dataKey="R" stroke="var(--red)" fill="var(--red)" fillOpacity={0.15} />
                    <Radar name="G" dataKey="G" stroke="var(--green)" fill="var(--green)" fillOpacity={0.15} />
                    <Radar name="B" dataKey="B" stroke="var(--blue)" fill="var(--blue)" fillOpacity={0.15} />
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ HISTOGRAM ══ */}
      {tab === 'histogram' && channelHist.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Panel title="CHANNEL INTENSITY DISTRIBUTIONS" badge="RGB + Grayscale" accent="var(--blue)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={channelHist}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="bin" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'Intensity (0-255)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 10 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="R" stroke="var(--red)" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="G" stroke="var(--green)" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="B" stroke="var(--blue)" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="Gray" stroke="var(--t2)" strokeWidth={1} dot={false} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {[['R', 'var(--red)'], ['G', 'var(--green)'], ['B', 'var(--blue)'], ['Gray', 'var(--t2)']].map(([ch, color]) => (
              <Panel key={ch} title={`${ch === 'Gray' ? 'Luminance' : ch + ' Channel'} Distribution`} accent={color}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={channelHist.filter((_, i) => i % 2 === 0)}>
                    <XAxis dataKey="bin" stroke="var(--t3)" tick={{ fontSize: 8 }} interval={7} />
                    <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                    <Tooltip />
                    <Bar dataKey={ch} fill={color} opacity={0.8} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            ))}
          </div>
        </div>
      )}

      {/* ══ PIXEL GRID ══ */}
      {tab === 'pixels' && pixelGrid.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '14px' }}>
          <Panel title="PIXEL VISUALIZATION" badge={`${pixelGridSize}×${pixelGridSize} grid`} accent="var(--blue)">
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '10px', color: 'var(--t3)' }}>GRID SIZE: {pixelGridSize}×{pixelGridSize}</span>
              <input type="range" min={8} max={48} step={4} value={pixelGridSize} onChange={e => setPixelGridSize(parseInt(e.target.value))} style={{ width: '150px', accentColor: 'var(--cyan)' }} />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${pixelGridSize}, 1fr)`,
              gap: '1px',
              background: 'var(--b0)',
              borderRadius: '3px',
              overflow: 'hidden',
              maxWidth: '600px',
            }}>
              {pixelGrid.map((px, i) => (
                <div key={i}
                  onMouseEnter={() => setHoveredPixel(px)}
                  onMouseLeave={() => setHoveredPixel(null)}
                  style={{
                    background: `rgb(${px.r},${px.g},${px.b})`,
                    aspectRatio: '1',
                    cursor: 'crosshair',
                    transition: 'transform .1s',
                  }}
                  title={`(${px.x},${px.y}) R:${px.r} G:${px.g} B:${px.b}`}
                />
              ))}
            </div>
          </Panel>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {hoveredPixel && (
              <Panel title="PIXEL INSPECTOR" accent="var(--cyan)">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <Metric label="Position X" value={hoveredPixel.x} color="var(--t2)" />
                  <Metric label="Position Y" value={hoveredPixel.y} color="var(--t2)" />
                  <Metric label="Red" value={hoveredPixel.r} color="var(--red)" />
                  <Metric label="Green" value={hoveredPixel.g} color="var(--green)" />
                  <Metric label="Blue" value={hoveredPixel.b} color="var(--blue)" />
                  <Metric label="Luma" value={Math.round(0.299*hoveredPixel.r + 0.587*hoveredPixel.g + 0.114*hoveredPixel.b)} color="var(--t2)" />
                </div>
                <div style={{ padding: '20px', borderRadius: '3px', background: `rgb(${hoveredPixel.r},${hoveredPixel.g},${hoveredPixel.b})`, border: '1px solid var(--b1)', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: hoveredPixel.r + hoveredPixel.g + hoveredPixel.b > 350 ? '#000' : '#fff', fontFamily: 'Syne' }}>
                    #{hoveredPixel.r.toString(16).padStart(2,'0')}{hoveredPixel.g.toString(16).padStart(2,'0')}{hoveredPixel.b.toString(16).padStart(2,'0')}
                  </div>
                </div>
                <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                  {[['R', hoveredPixel.r, 'var(--red)'], ['G', hoveredPixel.g, 'var(--green)'], ['B', hoveredPixel.b, 'var(--blue)']].map(([ch, v, c]) => (
                    <div key={ch} style={{ flex: 1 }}>
                      <div style={{ height: `${(v/255)*60}px`, background: c, borderRadius: '2px', minHeight: '2px', transition: 'height .2s' }} />
                      <div style={{ fontSize: '9px', color: c, textAlign: 'center', marginTop: '3px' }}>{ch}: {v}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            <Panel title="PIXEL INTENSITY HEATMAP">
              <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '10px' }}>Luminance map of the {pixelGridSize}×{pixelGridSize} grid:</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${pixelGridSize}, 1fr)`,
                gap: '1px',
                borderRadius: '3px',
                overflow: 'hidden',
              }}>
                {pixelGrid.map((px, i) => {
                  const luma = 0.299*px.r + 0.587*px.g + 0.114*px.b;
                  return (
                    <div key={i} style={{
                      background: `rgba(0,217,255,${luma/255})`,
                      aspectRatio: '1',
                    }} />
                  );
                })}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '8px' }}>Cyan intensity = luminance (dark → bright)</div>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ COMPARE ══ */}
      {tab === 'compare' && imgEl && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Panel title="MULTI-FILTER COMPARISON" badge="all at once">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
              {FILTERS.slice(0, 12).map(f => {
                const src = f.id === 'original' ? imgSrc : (imgEl ? applyFilter(imgEl, f.id, { brightness: 1.3, contrast: 1.5, threshold: 128, hue: 90, blockSize: 10, noise: 30 }) : null);
                return (
                  <div key={f.id} onClick={() => { setActiveFilter(f.id); setTab('filters'); }}
                    style={{ cursor: 'pointer' }}>
                    <div style={{ fontSize: '10px', color: f.color, letterSpacing: '.07em', marginBottom: '5px', textAlign: 'center' }}>{f.label}</div>
                    {src && <img src={src} alt={f.label} style={{ width: '100%', borderRadius: '2px', border: `1px solid ${f.color}44`, display: 'block', transition: 'border-color .15s' }} />}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '12px', fontSize: '10px', color: 'var(--t3)' }}>Click any preview to open it in the Filters tab with full parameter control.</div>
          </Panel>
        </div>
      )}
    </div>
  );
}
